
import { Thermometer } from 'lucide-react';
import { formatNumber } from '@/utils/formatNumber';
import { IconTitle } from '@/components/ui/icon-title';

interface EnvironmentCardProps {
  temperature: {
    current: number;
    average: number;
  };
  humidity: {
    current: number;
    average: number;
  };
  sensorStatus: {
    tempHumidity: {
      online: number;
      total: number;
    };
    iaq: {
      online: number;
      total: number;
    };
  };
}

export function IndoorEnvironmentCard({
  temperature,
  humidity,
  sensorStatus
}: EnvironmentCardProps) {
  return (
    <div className="flex flex-col bg-white rounded-[10px] shadow-card p-2 gap-1.5 border border-light-grey">
      {/* Header */}
      <IconTitle icon={Thermometer} title="Indoor Environment" />

      {/* Sensor Status - Compact Style */}
      <div className="flex flex-col gap-1.5 mb-2">
        <div className="flex items-center justify-between">
          <span className="paragraph-9 text-dark-grey">Temp/Humid Sensors</span>
          <div className="flex gap-1">
            {/* Online Status */}
            <div className="w-[65px] px-1.5 py-0.5 rounded-[4px] flex items-center justify-between gap-1 bg-success/20">
              <div className="flex items-center gap-1">
                <div className="w-1 h-1 rounded-full bg-[#14B8B4]" />
                <span className="text-[9px] text-black">Online</span>
              </div>
              <span className="text-[9px] font-semibold text-dark-green">
                {sensorStatus.tempHumidity.online}
              </span>
            </div>
            {/* Offline Status */}
            <div className="w-[65px] px-1.5 py-0.5 rounded-[4px] flex items-center justify-between gap-1 bg-gray-200/40">
              <div className="flex items-center gap-1">
                <div className="w-1 h-1 rounded-full bg-[#B4B4B4]" />
                <span className="text-[9px] text-black">Offline</span>
              </div>
              <span className="text-[9px] font-semibold text-dark-grey">
                {sensorStatus.tempHumidity.total - sensorStatus.tempHumidity.online}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <span className="paragraph-9 text-dark-grey">IAQ Sensors</span>
          <div className="flex gap-1">
            {/* Online Status */}
            <div className="w-[65px] px-1.5 py-0.5 rounded-[4px] flex items-center justify-between gap-1 bg-success/20">
              <div className="flex items-center gap-1">
                <div className="w-1 h-1 rounded-full bg-[#14B8B4]" />
                <span className="text-[9px] text-black">Online</span>
              </div>
              <span className="text-[9px] font-semibold text-dark-green">
                {sensorStatus.iaq.online}
              </span>
            </div>
            {/* Offline Status */}
            <div className="w-[65px] px-1.5 py-0.5 rounded-[4px] flex items-center justify-between gap-1 bg-gray-200/40">
              <div className="flex items-center gap-1">
                <div className="w-1 h-1 rounded-full bg-[#B4B4B4]" />
                <span className="text-[9px] text-black">Offline</span>
              </div>
              <span className="text-[9px] font-semibold text-dark-grey">
                {sensorStatus.iaq.total - sensorStatus.iaq.online}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col gap-1">
        {/* Temperature and Humidity */}
        <div className="pt-[5px] pb-[5px] pl-[6px] pr-[6px] bg-bg border border-light-grey rounded-[6px] flex flex-col">
          <div className="flex flex-col gap-1">
            {/* <div className="paragraph-11 text-black">Sensor Indoor</div> */}
            <div className="flex gap-3 flex-1">
              <div className="flex-1 flex flex-col justify-between">
                <div>
                  <div className="paragraph-11 text-dark">Room Temp</div>
                  <div className="flex items-center gap-2">
                    <span className="heading-6 text-black">{formatNumber(temperature.current)}</span>
                    <span className="paragraph-12 text-black">°C</span>
                  </div>
                </div>
                <div className="h-3 flex items-center gap-1">
                  <span className="paragraph-9 text-dark-grey">Today Avg.</span>
                  <span className="paragraph-9 text-gray-500">{formatNumber(temperature.average)} °C</span>
                </div>
              </div>
              {/* Thin separator line */}
              <div className="w-px bg-light-grey self-stretch mx-1"></div>
              <div className="flex-1 flex flex-col justify-between">
                <div>
                  <div className="paragraph-11 text-dark">Relative Humidity</div>
                  <div className="flex items-center gap-2">
                    <span className="heading-6 text-black">{formatNumber(humidity.current)}</span>
                    <span className="paragraph-12 text-black">%</span>
                  </div>
                </div>
                <div className="h-3 flex items-center gap-1">
                  <span className="paragraph-9 text-dark-grey">Today Avg.</span>
                  <span className="paragraph-9 text-gray-500">{formatNumber(humidity.average)}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
